/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import React, { useContext, useRef, useState } from 'react';
import { SessionContext } from '../../context/SessionContext';
import { buildNativeDownloadUrl } from '../../services/UrlService';
import { DownloadInstructions } from '../JoinPages/GoToMeetingPages/DownloadInstructions/DownloadInstructions';
import {
  initiateStatusPolling,
  Status as LaunchStatus,
} from '../../services/LaunchStatusService';
import { InstallPage } from './InstallPage/InstallPage';
import { Ongoing } from '../StatusPages/Ongoing/Ongoing';
import { Error } from '../StatusPages/Error/Error';
import { GoToDownloadInstructions } from './GoToPages/DownloadInstructions/GoToDownloadInstructions';
import { GoToInstallPage } from './GoToPages/InstallPage/GoToInstallPage';
import { GoToInstallError } from '../StatusPages/Error/GoToInstallError';
import { InstallSuccessComponent } from '../StatusPages/Success/InstallSuccessComponent';
import { reloadPageWithSessionTrackingId } from '../../services/NavigatorService';
import { ThemeType } from '../../services/IndexPageReaderService';
import { GoToInstallOngoing } from './GoToPages/Ongoing/GoToInstallOngoing';
import { GoToInstallSuccess } from './GoToPages/Success/GoToInstallSuccess';

export enum InstallFlowState {
  offeringInstall,
  downloading,
  ongoing,
  installSuccessful,
  error,
}

export const InstallFlow: React.FC = () => {
  const {
    meetingId,
    launcherServiceLaunchToken,
    openerLaunchToken,
    theme,
    sessionTrackingId,
  } = useContext(SessionContext);
  const nativeDownloadUrl = buildNativeDownloadUrl(openerLaunchToken);
  const [installFlowState, setInstallFlowState] = useState<InstallFlowState>(
    InstallFlowState.offeringInstall
  );
  const installFlowRef = useRef(installFlowState);
  installFlowRef.current = installFlowState;
  const launcherServiceLaunchTokenRef = useRef(launcherServiceLaunchToken);
  launcherServiceLaunchTokenRef.current = launcherServiceLaunchToken;

  const updateLaunchState = (newState: LaunchStatus) => {
    if (newState === LaunchStatus.Success) {
      setInstallFlowState(InstallFlowState.installSuccessful);
    } else if (newState === LaunchStatus.LaunchedApp) {
      setInstallFlowState(InstallFlowState.ongoing);
    } else if (newState === LaunchStatus.Error) {
      setInstallFlowState(InstallFlowState.error);
    }
  };

  const gotoPages: { [key in InstallFlowState]: JSX.Element } = {
    [InstallFlowState.offeringInstall]: (
      <GoToInstallPage
        onDownloadButtonClick={() => {
          window.setTimeout(
            () =>
              initiateStatusPolling(
                launcherServiceLaunchTokenRef.current,
                updateLaunchState
              ),
            200
          );
          setInstallFlowState(InstallFlowState.downloading);
        }}
        children
      />
    ),
    [InstallFlowState.downloading]: <GoToDownloadInstructions />,
    [InstallFlowState.ongoing]: <GoToInstallOngoing />,
    [InstallFlowState.installSuccessful]: <GoToInstallSuccess />,
    [InstallFlowState.error]: <GoToInstallError />,
  };

  const gotoMeetingPages: { [key in InstallFlowState]: JSX.Element } = {
    [InstallFlowState.offeringInstall]: (
      <InstallPage
        nativeDownloadUrl={nativeDownloadUrl}
        onDownloadButtonClick={() => {
          window.setTimeout(
            () =>
              initiateStatusPolling(
                launcherServiceLaunchTokenRef.current,
                updateLaunchState
              ),
            200
          );
          setInstallFlowState(InstallFlowState.downloading);
        }}
        children
      />
    ),
    [InstallFlowState.downloading]: (
      <DownloadInstructions nativeDownloadUrl={nativeDownloadUrl} />
    ),
    [InstallFlowState.ongoing]: (
      <Ongoing
        onTryAgain={() =>
          reloadPageWithSessionTrackingId(meetingId, sessionTrackingId)
        }
      />
    ),
    [InstallFlowState.installSuccessful]: <InstallSuccessComponent />,
    [InstallFlowState.error]: <Error />,
  };

  type ThemeToStateSelection = {
    [key in ThemeType]: { [key: string]: JSX.Element };
  };

  const ThemeToInstallStateSelection: ThemeToStateSelection = {
    [ThemeType.GoToTheme]: gotoPages,
    [ThemeType.GoToMeetingTheme]: gotoMeetingPages,
  };
  const InstallStateSelection = ThemeToInstallStateSelection[theme.themeType];

  return <>{InstallStateSelection[installFlowState]}</>;
};
